<template>
    <div>
        <limitless-page-header :title="'Såplan'" :sub-title="plan_name"></limitless-page-header>

        <div class="content">
            <div class="row mb-3">
                <div class="col-12 col-md-3">
                    <v-date-picker
                        v-model="date"
                        mode="date"
                        :locale="{ id: 'sv', firstDayOfWeek: 2, masks: { weekdays: 'WW', input: 'YYYY-MM-DD' } }"
                        :disabled-dates='{ weekdays: [1, 3, 4, 5, 6, 7] }'
                        :model-config="{ type: 'string', mask: 'YYYY-MM-DD' }"
                        show-iso-weeknumbers
                    >
                        <template v-slot="{ inputValue, togglePopover }">
                            <input class="form-control" :value="inputValue" @focus="togglePopover()">
                        </template>
                    </v-date-picker>
                </div>
            </div>

            <div v-if="busy_loading">
                <i class="icon-spinner2 spinner"></i>
            </div>
            <div v-else class="row">
                <div class="col-12">
                    <div class="card">
                        <div class="card-header bg-white">
                            <h5 class="card-title">Såplan</h5>
                        </div>
                        <div v-if="plan && weekdays.length > 0" class="card-table" style="border-bottom-style: solid; border-bottom-color: #186632 !important; border-bottom-width: 2px;">
                            <table class="table table-striped table-xs">
                                <tbody>
                                    <tr>
                                        <th>Gröda</th>
                                        <th>Antal</th>
                                        <th>Flytt till barnkammare</th>
                                        <th>Glesning och flytt till tillväxtzon</th>
                                        <th>Skörda</th>
                                    </tr>
                                </tbody>
                                <tbody v-for="day in weekdays" :key="'day-' + day.date">
                                    <tr style="border-top-style: solid; border-top-color: #186632 !important; border-top-width: 2px;">
                                        <th colspan="5"><span style="font-size: 1rem;">{{ day.day }} {{ day.date }}</span></th>
                                    </tr>
                                    <template v-if="!(plan.main.todo && plan.main.todo[day.date]) && !(plan.extra.todo && plan.extra.todo[day.date])">
                                        <tr>
                                            <td colspan="5"><i>Inga grödor att så</i></td>
                                        </tr>
                                    </template>
                                    <template v-if="plan.main.todo && plan.main.todo[day.date]">
                                        <tr>
                                            <th colspan="5">{{ plan.main._metadata.unit.name }}</th>
                                        </tr>
                                        <tr v-for="crop in plan.main.todo[day.date]" :key="'crop-' + day.date + '-' + crop.id">
                                            <th>{{ crop.name }}</th>
                                            <td>{{ crop.quantity }}</td>
                                            <td>
                                                <span v-if="day.date === crop.nursery_date"><i class="icon-arrow-right8"></i></span>
                                                <span v-else>{{ crop.nursery_date }}</span>
                                            </td>
                                            <td>
                                                <div v-for="(td, td_idx) in crop.transplant_date" :key="'td' + td_idx">{{ td.date }}: {{ td.quantity }} st</div>
                                            </td>
                                            <td>
                                                <div v-for="(hd, hd_idx) in crop.harvest_date" :key="'hd' + hd_idx">{{ hd.date }}: {{ hd.quantity }} st</div>
                                            </td>
                                        </tr>
                                    </template>
                                    <template v-if="plan.extra.todo && plan.extra.todo[day.date]">
                                        <tr v-if="plan.main.todo && plan.main.todo[day.date]">
                                            <th colspan="5">&nbsp;</th>
                                        </tr>
                                        <tr>
                                            <th colspan="5">{{ plan.extra._metadata.unit.name }}</th>
                                        </tr>
                                        <tr v-for="crop in plan.extra.todo[day.date]" :key="'crop-' + day.date + '-' + crop.id">
                                            <th>{{ crop.name }}</th>
                                            <td>{{ crop.quantity }}</td>
                                            <td>
                                                <span v-if="day.date === crop.nursery_date"><i class="icon-arrow-right8"></i></span>
                                                <span v-else>{{ crop.nursery_date }}</span>
                                            </td>
                                            <td>
                                                <div v-for="(td, td_idx) in crop.transplant_date" :key="'td' + td_idx">{{ td.date }}: {{ td.quantity }} st</div>
                                            </td>
                                            <td>
                                                <div v-for="(hd, hd_idx) in crop.harvest_date" :key="'hd' + hd_idx">{{ hd.date }}: {{ hd.quantity }} st</div>
                                            </td>
                                        </tr>
                                    </template>
                                </tbody>
                            </table>
                        </div>
                        <div v-else class="card-body">
                            <i>Hittade ingen plan...</i>
                        </div>
                    </div> <!-- /card -->
                </div>
            </div>
        </div> <!-- /content -->
    </div>
</template>

<script>
import apiClient from '@services/ApiClient';
import LimitlessPageHeader from '@components/Limitless/LimitlessPageHeader';
import VDatePicker from 'v-calendar/lib/components/date-picker.umd';
import { startOfWeek } from '@services/Helpers';

export default {
    name: 'SowPlan',
    components: {
        LimitlessPageHeader,
        VDatePicker,
    },
    data: () => ({
        busy_loading: true,
        date: null,
        plan: null,
    }),
    mounted() {
        this.date = startOfWeek(new Date()).toLocaleString('sv-SE').substr(0, 10);
    },
    methods: {
        getPlan() {
            this.busy_loading = true;
            apiClient.get('/vue/sowplan/' + this.date)
                .then((response) => {
                    this.plan = response.data;
                })
                .finally(() => {
                    this.busy_loading = false;
                });
        },
    },
    watch: {
        date() {
            this.getPlan();
        },
    },
    computed: {
        plan_name() {
            return this.date;
        },
        weekdays() {
            const arr = [];
            if (this.date) {
                const dateparts = this.date.split('-');
                if (dateparts) {
                    const date = startOfWeek(new Date(dateparts[0], dateparts[1] - 1, dateparts[2]));

                    // eslint-disable-next-line no-plusplus
                    for (let i = 0; i < 7; i++) {
                        arr.push({
                            date: date.toLocaleString('sv-SE').substr(0, 10),
                            day: date.toLocaleString('sv-SE', { weekday: 'long' }),
                        });
                        date.setDate(date.getDate() + 1);
                    }
                }
            }
            return arr;
        },
    },
};
</script>
